import React, { useCallback } from 'react';
import { connect } from 'react-redux';

import { Field, withFormik } from 'formik';

import { Alert, EditModal, FlexColumn, FormikInput } from '@portal/common/components';
import { ResourceList } from '@portal/common/types';

import api from '../../api';
import _ from '../../locale';
import { createResourceList, updateResourceList } from '../../store/resource-lists/actions';

const mapNameToLabel = (name: string) => _(`resource_list_field_label_${name}`);

const dispatchProps = {
    createResourceList: createResourceList,
    updateResourceList: updateResourceList,
};

type Props = typeof dispatchProps & {
    className?: string;
    style?: React.CSSProperties;
    item?: ResourceList;
    isVisible?: boolean;
    onClose?: () => void;
    onSuccess?: () => void;
    children?: (renderProps: RenderProps) => React.ReactNode;
};

type FormValues = Pick<ResourceList, 'name' | 'description'> & {};

type RenderProps = {
    closeModal: () => void;
    openModal: () => void;
};

const EditResourceListModal = withFormik<Props, FormValues>({
    enableReinitialize: true,
    // initialize values
    mapPropsToValues: ({ item: { name, description } = {} }) => ({
        name: name || '',
        description: description || '',
    }),
    handleSubmit: (values, form) => {
        const { item, createResourceList, updateResourceList, onSuccess } = form.props;

        let onSubmit;

        const isAddingNew = item == null;

        if (isAddingNew) {
            onSubmit = () =>
                api.organizationResourceList
                    .createOrganizationResourceList({ item: values })
                    .then(createResourceList);
        } else {
            onSubmit = () =>
                api.organizationResourceList
                    .updateOrganizationResourceList({ listId: item.id!, item: values })
                    .then(updateResourceList);
        }

        return onSubmit()
            .then(() => {
                form.setStatus({ isVisible: false });
                form.resetForm();
                onSuccess && onSuccess();
            })
            .catch((err) => {
                form.setStatus({ error: err.message, isVisible: true });
                if (err.fields) {
                    form.setErrors(err.fields);
                }
            })
            .finally(() => {
                form.setSubmitting(false);
            });
    },
})(({
    className,
    style,
    children,
    isVisible,
    onClose,
    isSubmitting,
    dirty,
    status = { isVisible: false },
    item,
    handleSubmit,
    setStatus,
    resetForm,
}) => {
    const isNew = item == null;

    const renderProps: RenderProps = {
        closeModal: () => {
            setStatus({ isVisible: false });
        },
        openModal: () => {
            setStatus({ isVisible: true });
        },
    };

    const handleCloseModal = useCallback(() => {
        onClose != null ? onClose() : renderProps.closeModal();
        resetForm();
    }, [onClose, resetForm]);

    return (
        <div {...{ className, style }}>
            {children && children(renderProps)}
            <EditModal
                title={_.get(isNew ? 'lists_add_new_modal_title' : 'lists_edit_modal_title')}
                isVisible={isVisible != null ? isVisible : status.isVisible}
                isSubmitting={isSubmitting}
                isSaveDisabled={!dirty}
                onClose={handleCloseModal}
                onSubmit={handleSubmit}
            >
                {status && status.error && <Alert color="error">{status.error}</Alert>}

                <FlexColumn>
                    <Field
                        name="name"
                        component={FormikInput}
                        mapNameToLabel={mapNameToLabel}
                        type="text"
                        required
                        autoFocus
                    />
                    <Field
                        name="description"
                        component={FormikInput}
                        mapNameToLabel={mapNameToLabel}
                        type="text"
                    />
                </FlexColumn>
            </EditModal>
        </div>
    );
});

export default connect(null, dispatchProps)(EditResourceListModal);
